import React, { useRef, useState } from 'react';
import { PenLine, Check, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { capUpdateProject } from '../../lib/capstoneApi';
import { PHASE_STATUS_COLORS, StatusBadge, fmtDate, inputCls, btnGhost } from './shared';
import RefineBar from './RefineBar';

const CHARTER_LABELS = {
  background: 'Background & context', objectives: 'Objectives', scope: 'Scope & boundaries',
  success_criteria: 'Success criteria / KPIs', beneficiary: 'Beneficiary / stakeholder',
  deliverables: 'Expected deliverables', assumptions: 'Assumptions & constraints',
};

export default function OverviewTab({ project, tasks, risks, reloadProject }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(project.charter || {});
  const timer = useRef(null);
  const canEdit = ['lead', 'member', 'admin'].includes(project.my_role);

  const autosave = (next) => {
    setDraft(next);
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try { await capUpdateProject(project.id, { charter: next }); }
      catch (e) { toast.error(e.response?.status === 403 ? 'Only the team can edit the charter' : 'Autosave failed'); }
    }, 900);
  };

  const doneTasks = tasks.filter((t) => t.status === 'Done').length;
  const openRisks = risks.filter((r) => r.status !== 'Closed').length;
  const atRisk = project.at_risk_milestones || [];

  return (
    <div className="grid lg:grid-cols-[1.5fr,1fr] gap-6" data-testid="cap-overview-tab">
      <div className="space-y-6">
        <div className="rounded-2xl border border-white/10 bg-slate-900/50 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="text-[10px] tracking-[0.25em] font-bold uppercase text-amber-300">Project charter</div>
            {canEdit && (
              <button className={btnGhost} onClick={() => { setEditing((v) => !v); setDraft(project.charter || {}); if (editing) reloadProject(); }} data-testid="cap-charter-edit-btn">
                {editing ? <><Check className="w-3.5 h-3.5" /> Done</> : <><PenLine className="w-3.5 h-3.5" /> Edit</>}
              </button>
            )}
          </div>
          {editing && (
            <RefineBar
              getFields={() => draft}
              context={{ title: project.title, domain: project.domain, sponsor_org: project.sponsor_org }}
              onApply={(f) => autosave({ ...draft, ...f })}
              label="Refine my charter to CXO-ready language"
            />
          )}
          {project.problem_statement && (
            <p className="font-serif italic text-white/85 text-[15px] mb-5 leading-relaxed">“{project.problem_statement}”</p>
          )}
          <div className="space-y-4">
            {Object.entries(CHARTER_LABELS).map(([k, label]) => (
              <div key={k}>
                <div className="text-[10px] tracking-[0.18em] font-bold uppercase text-white/40 mb-1">{label}</div>
                {editing ? (
                  <textarea rows={2} className={`${inputCls} resize-y`} value={draft[k] || ''} onChange={(e) => autosave({ ...draft, [k]: e.target.value })} data-testid={`cap-overview-charter-${k}`} />
                ) : (
                  <p className="text-[13px] text-white/70 leading-relaxed whitespace-pre-wrap">{(project.charter || {})[k] || <span className="text-white/25 italic">Not filled yet</span>}</p>
                )}
              </div>
            ))}
          </div>
          {editing && <div className="text-[10px] text-emerald-300/60 mt-3 italic">Autosaving as you type…</div>}
        </div>
      </div>

      <div className="space-y-6">
        <div className="grid grid-cols-3 gap-3">
          {[[`${doneTasks}/${tasks.length}`, 'Tasks done'], [project.days_left !== null ? Math.max(project.days_left, 0) : '—', 'Days left'], [openRisks, 'Open risks']].map(([v, l]) => (
            <div key={l} className="rounded-xl border border-white/10 bg-slate-900/50 p-4 text-center">
              <div className="font-serif text-2xl text-amber-300">{v}</div>
              <div className="text-[10px] tracking-[0.14em] uppercase text-white/45 mt-1">{l}</div>
            </div>
          ))}
        </div>

        <div className="rounded-2xl border border-white/10 bg-slate-900/50 p-5" data-testid="cap-overview-timeline">
          <div className="text-[10px] tracking-[0.25em] font-bold uppercase text-white/50 mb-4">Phase timeline</div>
          {(project.milestones || []).map((m) => (
            <div key={m.id} className="mb-3.5 last:mb-0">
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="text-[12px] text-white/80 font-medium truncate">{m.name}</span>
                <span className="flex items-center gap-1.5 shrink-0">
                  {atRisk.includes(m.id) && <ShieldAlert className="w-3.5 h-3.5 text-amber-300" title="At risk" />}
                  <StatusBadge status={m.status} map={PHASE_STATUS_COLORS} />
                </span>
              </div>
              <div className="h-1.5 rounded-full bg-white/8 overflow-hidden">
                <div className={`h-full rounded-full ${m.status === 'Done' ? 'bg-emerald-400 w-full' : m.status === 'In Progress' ? 'bg-amber-300 w-1/2' : m.status === 'Blocked' ? 'bg-rose-400 w-1/3' : 'w-0'}`} />
              </div>
              <div className="text-[9.5px] text-white/30 mt-0.5">{fmtDate(m.planned_start)} → {fmtDate(m.planned_end)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
